/**
 * «Загрузить свою картинку»: кнопка и перетаскивание — ОДНО действие с одним состоянием.
 *
 * Две копии этой логики разошлись бы на отказах — кнопка сказала бы про 5 МБ, а
 * перетаскивание промолчало, — поэтому владелец состояния загрузки ровно один, и оба входа
 * (`pick()` и `dropProps`) ведут в одну функцию.
 *
 * ПУТЬ ЦЕЛИКОМ: проверки в браузере → `POST /api/v1/media` сырыми байтами → `media_id` →
 * `POST /api/v1/drafts/:id/versions` с `media: {media_id}` → `onDone()`. Картинка человека
 * прикрепляется СРАЗУ (v+1), а не становится предложением: предложение — это то, что
 * прислал сервис и по чему человек ещё не решил, а здесь решение уже принято — оно и есть
 * сам факт загрузки. Гейт утверждения такую картинку не трогает вовсе.
 *
 * ПОЧЕМУ ПОТОЛОК 5 МиБ ПРОВЕРЯЕТСЯ И ЗДЕСЬ. Не вместо сервера, а раньше него: `bodyLimit`
 * маршрута обрывает соединение, и человек получает «сеть недоступна» вместо «файл тяжёлый».
 * Число берётся из контракта (`HUMAN_UPLOAD_MAX_BYTES`), а не пишется цифрой — иначе оно
 * разойдётся с маршрутом молча.
 */

import { type DraftContent, HUMAN_UPLOAD_MAX_BYTES, isMediaMime } from '@smm/contracts';
import {
  type ChangeEvent,
  type DragEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { postBytes } from '../../api/client.ts';
import { isStaleBaseVersion, saveErrorText, saveVersion } from '../versionSave.ts';

/** Ответ `POST /api/v1/media`. Нужен один ключ; остальные бэкенд отдаёт для журнала. */
interface UploadedMedia {
  readonly media_id: string;
}

export interface UseVisualUploadArgs {
  readonly draftId: string;
  readonly versionNo: number;
  /** Текст и сущности сохранённой версии: правка картинки не меняет ни одного знака поста. */
  readonly canonical: Pick<DraftContent, 'text' | 'entities'>;
  /** Можно ли вообще класть картинку в эту версию. */
  readonly editable: boolean;
  /**
   * Почему нельзя — словами, по состоянию версии (`lockReason`, `routes/versionLock.ts`).
   * Не задано — причина не названа: одной фразой «выпущен ключ публикации» про любую
   * неправимую версию хук больше не отвечает (ревью 19.09.2026, ui-11).
   */
  readonly lockedBecause?: string | null | undefined;
  readonly onDone: () => void;
}

export interface VisualUpload {
  readonly dragging: boolean;
  readonly busy: boolean;
  /** Что сейчас происходит: «Загружаем картинку…» / «Сохраняем версию с картинкой…». */
  readonly stage: string | null;
  readonly error: string | null;
  readonly notice: string | null;
  readonly dropProps: {
    readonly onDragOver: (e: DragEvent<HTMLElement>) => void;
    readonly onDragLeave: (e: DragEvent<HTMLElement>) => void;
    readonly onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  /** Клавиатурный путь: клик по скрытому `<input type="file">`. */
  readonly pick: () => void;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  readonly accept: string;
}

const MB = 1024 * 1024;
export const UPLOAD_ACCEPT = 'image/png,image/jpeg';

const fmtMb = (bytes: number): string =>
  (bytes / MB).toLocaleString('ru-RU', { maximumFractionDigits: 1 });

/**
 * Проверки ДО сети, каждая со своей фразой. Возвращает отказ или `null`.
 *
 * Тип берётся из самого файла (`File.type`), а не из расширения имени: браузер ставит его по
 * системной таблице, и переименованный `.png` придёт сюда как `text/plain` — то есть первая
 * же проверка ловит ровно тот случай, ради которого сервер сверяет магические байты.
 */
export function uploadRejection(file: File): string | null {
  // PDF, отпущенный на пост мимо полосы «Файл» (docs/release-2026-09-19.md §9.2), — не ошибка
  // формата, а промах местом: «это не картинка» назвало бы следствие, а не то, что делать.
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    return `«${file.name}» — PDF, а не картинка. Файл крепится к посту полосой «Файл» под полосой визуала: перетащите его туда или нажмите «Прикрепить PDF…».`;
  }
  if (!isMediaMime(file.type)) {
    const type = file.type === '' ? 'тип не определён' : `тип ${file.type}`;
    return `Это не картинка: нужен PNG или JPEG, а у файла «${file.name}» ${type}.`;
  }
  if (file.size === 0) return `Файл «${file.name}» пустой — 0 байт.`;
  if (file.size > HUMAN_UPLOAD_MAX_BYTES) {
    return `Файл ${fmtMb(file.size)} МБ, потолок — ${fmtMb(HUMAN_UPLOAD_MAX_BYTES)} МБ. Уменьшите картинку и попробуйте снова.`;
  }
  return null;
}

export function useVisualUpload(a: UseVisualUploadArgs): VisualUpload {
  const { draftId, versionNo, canonical, editable, onDone } = a;
  const lockedBecause = a.lockedBecause ?? null;
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acRef = useRef<AbortController | null>(null);
  /**
   * Глубина перетаскивания. `dragleave` приходит и при переходе между ДЕТЬМИ зоны сброса,
   * поэтому «ушли» по одному событию гасило бы подсветку на каждой внутренней границе.
   */
  const depth = useRef(0);

  useEffect(
    () => () => {
      acRef.current?.abort();
    },
    [],
  );

  const upload = useCallback(
    (files: readonly File[]): void => {
      if (busy) return;
      setError(null);
      setNotice(null);
      const file = files[0];
      if (file === undefined) return;
      if (!editable) {
        setError(
          lockedBecause === null
            ? 'Картинку к этой версии не положить: версия не правится.'
            : `Картинку к этой версии не положить: ${lockedBecause}.`,
        );
        return;
      }
      // Фраза про «взят первый» переживает успех загрузки: перетёртая итоговым сообщением,
      // она означала бы, что второй файл исчез молча.
      const multi =
        files.length > 1
          ? `Взят первый файл из ${String(files.length)}: за раз ставится одна картинка. `
          : '';
      if (multi !== '') setNotice(multi.trim());
      const bad = uploadRejection(file);
      if (bad !== null) {
        setError(bad);
        return;
      }

      acRef.current?.abort();
      const ac = new AbortController();
      acRef.current = ac;
      setBusy(true);
      setStage('Загружаем картинку…');
      postBytes<UploadedMedia>('/api/v1/media', file, ac.signal)
        .then((media) => {
          if (ac.signal.aborted) return null;
          setStage('Сохраняем версию с картинкой…');
          // Текст и сущности — из СОХРАНЁННОЙ версии побайтово: загрузка картинки не имеет
          // права поменять ни одного знака поста, а набранная и не сохранённая правка
          // осталась бы незамеченной, если бы уехала сюда.
          return saveVersion({
            draftId,
            text: canonical.text,
            entities: canonical.entities,
            media: { media_id: media.media_id },
            baseVersionNo: versionNo,
          });
        })
        .then((saved) => {
          if (ac.signal.aborted || saved === null) return;
          setNotice(
            `${multi}Картинка сохранена как v${String(saved.version_no)}. Пост уйдёт с ней, первым блоком.`,
          );
          onDone();
        })
        .catch((e: unknown) => {
          if (ac.signal.aborted) return;
          if (isStaleBaseVersion(e)) {
            // Не ошибка, а подтверждение: чужая версия уже текущая, и экран надо
            // перезаполнить, иначе картинка легла бы поверх текста, которого человек не видел.
            setNotice(
              `Пока вы решали, появилась версия новее v${String(versionNo)}. Экран перезаполнен — ` +
                'решите по картинке заново.',
            );
            onDone();
            return;
          }
          setError(saveErrorText(e));
        })
        .finally(() => {
          if (ac.signal.aborted) return;
          setBusy(false);
          setStage(null);
        });
    },
    [busy, draftId, versionNo, canonical, editable, lockedBecause, onDone],
  );

  const pick = useCallback((): void => {
    inputRef.current?.click();
  }, []);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const list = e.target.files;
      const files = list === null ? [] : [...list];
      // Значение поля сбрасывается сразу: без этого второй выбор ТОГО ЖЕ файла не даёт
      // события `change` вовсе, и повтор после отказа выглядел бы как зависшая кнопка.
      e.target.value = '';
      upload(files);
    },
    [upload],
  );

  const onDragOver = useCallback((e: DragEvent<HTMLElement>): void => {
    // Без preventDefault браузер не породит `drop` вовсе — грабля описана в PlanPage.tsx.
    e.preventDefault();
    if (depth.current === 0) depth.current = 1;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    depth.current = 0;
    setDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>): void => {
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      // jsdom не реализует DataTransfer вовсе, поэтому тест передаёт литерал в init события;
      // отсюда мягкое чтение, а не `e.dataTransfer.files` в лоб.
      const dt = e.dataTransfer as DataTransfer | null;
      const list = dt?.files ?? null;
      upload(list === null ? [] : [...list]);
    },
    [upload],
  );

  return {
    dragging,
    busy,
    stage,
    error,
    notice,
    dropProps: { onDragOver, onDragLeave, onDrop },
    pick,
    inputRef,
    onInputChange,
    accept: UPLOAD_ACCEPT,
  };
}
